import Phaser from "phaser";
import { shouldRestartOnQuit } from "../flow/entry";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  createInfiniteWarState,
  drainEvents,
  playCard,
  tickInfiniteWar,
  type InfiniteWarState,
} from "../features/infinite-war";
import { cardAimHintKey } from "../features/infinite-war/sim/accessors";
import {
  BOARD,
  FIELD,
  HAND_MAX,
  HAND_UI,
  VIEW,
} from "../features/infinite-war/view/layout";
import {
  cancelCast,
  fieldConfirmIntent,
  IDLE_CAST,
  selectOrToggleCard,
  selectedCard,
  type CardCastSession,
} from "../features/infinite-war/view/card-cast-flow";
import { drawCards, destroyCardTexts, type CardsHudHost } from "../features/infinite-war/view/cards-hud";
import { formatTeamManaLine } from "../features/infinite-war/view/mana-hud";
import {
  createDemigodBarTexts,
  drawDemigodBars,
  type DemigodBarsHost,
} from "../features/infinite-war/view/demigod-bars";
import { drawDominionTiles } from "../features/infinite-war/view/tiles-view";
import {
  destroyUnitViews,
  drawDemigods,
  drawVeilSouls,
  syncAliveUnits,
  type DemigodView,
  type UnitView,
  type UnitsViewHost,
} from "../features/infinite-war/view/units-view";
import {
  playConversionFlash,
  playDemigodBurst,
  playFactionShockwave,
  playSoulFade,
  playSpellFx,
  playUnitAttack,
} from "../features/infinite-war/view/vfx-bridge";
import { sharedInput } from "../input/input-controller";
import { t } from "../data/strings";
import { getUiLayout } from "../ui/layout";
import { mountInfiniteWarHud } from "../ui/huds/infinite-war";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";
import { VfxPlayer } from "../vfx";

/** Thin scene: input + present orchestration + menu. Presentation lives in `view/*`. */
export class InfiniteWarScene extends Phaser.Scene {
  private state!: InfiniteWarState;
  private field!: Phaser.GameObjects.Graphics;
  private veilGfx!: Phaser.GameObjects.Graphics;
  private demigodGfx!: Phaser.GameObjects.Graphics;
  private demigodViews = new Map<"south" | "north", DemigodView>();
  private statusGfx!: Phaser.GameObjects.Graphics;
  private bars!: DemigodBarsHost;
  private manaText!: Phaser.GameObjects.Text;
  private cardsHost!: CardsHudHost;
  private hint!: Phaser.GameObjects.Text;
  private resultText!: Phaser.GameObjects.Text;
  private unitViews = new Map<number, UnitView>();
  private unitHpSeen = new Map<number, number>();
  private unitHpRevealUntil = new Map<number, number>();
  /** Unified select→aim→confirm; never spends mana on first card tap. */
  private castSession: CardCastSession = IDLE_CAST;
  private attackMotions = new Map<number, { until: number; dx: number; dy: number }>();
  private trailBudget = { left: 8 };
  private vfx!: VfxPlayer;
  private hud!: ReturnType<typeof mountInfiniteWarHud>;
  private menuOpen = false;
  private disposed = false;

  constructor() {
    super(SceneKey.InfiniteWar);
  }

  private unitsHost(): UnitsViewHost {
    return {
      scene: this,
      unitViews: this.unitViews,
      demigodViews: this.demigodViews,
      unitHpSeen: this.unitHpSeen,
      unitHpRevealUntil: this.unitHpRevealUntil,
      attackMotions: this.attackMotions,
      trailBudget: this.trailBudget,
      demigodGfx: this.demigodGfx,
      veilGfx: this.veilGfx,
    };
  }

  create(): void {
    this.disposed = false;
    this.menuOpen = false;
    this.castSession = IDLE_CAST;
    markActiveScene(SceneKey.InfiniteWar);
    sharedInput.attach();
    clearOverlay();
    getUiLayout().sync();
    this.state = createInfiniteWarState();
    this.vfx = new VfxPlayer(this);
    this.hud = mountInfiniteWarHud({
      onOpenMenu: () => this.openMenu(),
      onResume: () => this.resumeMenu(),
      onRestart: () => this.restartMatch(),
      onQuit: () => this.quitToTitle(),
    });
    this.hud.sync({ mode: "play", winner: null });
    this.cameras.main.setBackgroundColor(VIEW.colors.bg);
    this.field = this.add.graphics();
    this.veilGfx = this.add.graphics().setDepth(5);
    this.demigodGfx = this.add.graphics().setDepth(4);
    this.cardsHost = {
      scene: this,
      cardGfx: this.add.graphics().setDepth(10),
      cardTexts: [],
    };
    this.statusGfx = this.add.graphics().setDepth(10);
    const texts = createDemigodBarTexts(this);
    this.bars = { statusGfx: this.statusGfx, ...texts };
    this.manaText = this.add
      .text(270, HAND_UI.manaY, "", {
        fontFamily: "Georgia, serif",
        fontSize: "14px",
        color: "#9ee7ff",
      })
      .setOrigin(0.5)
      .setDepth(13);
    this.hint = this.add
      .text(270, HAND_UI.hintY, t("iw.hint.choose"), {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "12px",
        color: "#cbd5e1",
      })
      .setOrigin(0.5)
      .setDepth(13);
    this.resultText = this.add
      .text(270, 480, "", {
        fontFamily: "Georgia, serif",
        fontSize: "28px",
        color: "#fff1ca",
      })
      .setOrigin(0.5)
      .setDepth(20)
      .setVisible(false);
    this.input.on("pointerdown", this.onPointerDown, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
  }

  update(_time: number, dtMs: number): void {
    if (this.disposed) return;
    if (sharedInput.consumePressed().cancel) {
      this.onCancelPressed();
      return;
    }
    if (!this.menuOpen && !this.state.winner) {
      tickInfiniteWar(this.state, Math.min(0.05, dtMs / 1000));
      this.trailBudget.left = Number(VIEW.rig.trailBudgetPerFrame) || 8;
      const host = this.unitsHost();
      for (const event of drainEvents(this.state)) {
        if (event.kind === "demigod_burst") {
          playDemigodBurst(this, event.x, event.y, event.radius, event.team);
        }
        if (event.kind === "attack") {
          playUnitAttack(
            host,
            this.vfx,
            event.unitId,
            event.roleId,
            event.x,
            event.y,
            event.targetX,
            event.targetY,
            event.team,
            !!event.heal,
          );
        }
        if (event.kind === "shockwave") {
          playFactionShockwave(this, this.vfx, event.x, event.y, event.angle, event.team);
        }
        if (event.kind === "spell_fx") {
          playSpellFx(this, this.vfx, event.spell, event.x, event.y, event.team, {
            angle: event.angle,
            radius: event.radius,
            phase: event.phase ?? "impact",
          });
        }
        if (event.kind === "soul_fall") {
          playSoulFade(this, event.x, event.y, event.team);
        }
        if (event.kind === "soul_harvest") {
          playConversionFlash(this, event.x, event.y, event.team);
        }
        if (event.kind === "win") {
          this.menuOpen = false;
          this.resultText.setVisible(false);
        }
      }
    }
    this.draw();
    this.syncHud();
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.disposed || this.state.winner || this.menuOpen) return;
    // Right-click cancels aiming (same as Esc while selected).
    if (pointer.rightButtonDown() || pointer.button === 2) {
      if (this.castSession.phase === "aiming") {
        this.clearCast(t("iw.hint.cancelled"));
      }
      return;
    }
    const { x, y } = pointer;
    if (y >= HAND_UI.hitTop) {
      const index = Math.floor((x - (HAND_UI.originX - HAND_UI.cardW / 2)) / HAND_UI.pitch);
      if (index < 0 || index >= HAND_MAX) return;
      const card = this.state.cards.south.hand[index];
      if (!card) return;
      this.castSession = selectOrToggleCard(this.castSession, card);
      const selected = selectedCard(this.castSession);
      if (!selected) {
        this.hint.setText(t("iw.hint.choose"));
        return;
      }
      // Select only — never spend / resolve on first tap.
      this.hint.setText(t(cardAimHintKey(selected)));
      return;
    }
    if (y < FIELD.y || y > FIELD.y + FIELD.h) return;
    const intent = fieldConfirmIntent(this.castSession, x, y);
    if (!intent.ok) return;
    if (playCard(this.state, "south", intent.card, intent.targetX, intent.targetY)) {
      this.castSession = IDLE_CAST;
      // Summon aimSummon → played; spells / confirm → wave copy.
      this.hint.setText(
        intent.aim === "position" && cardAimHintKey(intent.card) === "iw.hint.aimSummon"
          ? t("iw.hint.played")
          : t("iw.hint.wave"),
      );
    } else {
      this.hint.setText(t("iw.hint.mana"));
    }
  }

  private clearCast(message?: string): void {
    this.castSession = cancelCast(this.castSession);
    this.hint.setText(message ?? t("iw.hint.choose"));
  }

  private draw(): void {
    const g = this.field;
    g.clear();
    g.fillStyle(0x111522).fillRoundedRect(BOARD.x, BOARD.y, BOARD.w, BOARD.h, 12);
    drawDominionTiles(g, this.state);
    const host = this.unitsHost();
    drawDemigods(host, this.state);
    drawVeilSouls(host, this.state);
    g.lineStyle(2, 0xd6b464, 0.35).strokeRoundedRect(BOARD.x, BOARD.y, BOARD.w, BOARD.h, 12);
    for (const wave of this.state.waves) {
      g.lineStyle(3, wave.team === "south" ? 0x8cf2ff : 0xffa8b2, 0.85);
      g.strokeCircle(wave.x, wave.y, wave.radius * 0.55);
    }
    for (const blast of this.state.pendingBlasts) {
      const pulse = 0.45 + Math.sin(this.state.time * 9 + blast.id) * 0.2;
      g.lineStyle(2, blast.team === "south" ? 0xffe08a : 0xb388ff, pulse);
      g.strokeCircle(blast.x, blast.y, blast.radius * 0.7);
    }
    syncAliveUnits(host, this.state);
    let sel = selectedCard(this.castSession);
    if (sel && !this.state.cards.south.hand.includes(sel)) {
      this.castSession = IDLE_CAST;
      sel = null;
    }
    drawDemigodBars(this.bars, this.state);
    this.manaText.setPosition(270, HAND_UI.manaY);
    this.manaText.setText(formatTeamManaLine(this.state, "south"));
    // Hint OR detail — never both in the mid band (detail owns aim copy when selected).
    this.hint.setVisible(!sel);
    this.hint.setPosition(270, HAND_UI.hintY);
    drawCards(this.cardsHost, this.state, sel, {
      aimHint: sel ? this.hint.text : null,
    });
  }

  private syncHud(): void {
    const mode = this.state.winner
      ? "ended"
      : this.menuOpen
        ? "paused"
        : "play";
    this.hud.sync({ mode, winner: this.state.winner });
  }

  private onCancelPressed(): void {
    if (this.state.winner) return;
    if (this.menuOpen) {
      this.resumeMenu();
      return;
    }
    if (this.castSession.phase === "aiming") {
      this.clearCast(t("iw.hint.cancelled"));
      return;
    }
    this.openMenu();
  }

  private openMenu(): void {
    if (this.disposed || this.state.winner || this.menuOpen) return;
    this.menuOpen = true;
    this.castSession = IDLE_CAST;
    this.syncHud();
  }

  private resumeMenu(): void {
    if (this.disposed || !this.menuOpen || this.state.winner) return;
    this.menuOpen = false;
    this.syncHud();
  }

  private restartMatch(): void {
    if (this.disposed) return;
    this.teardown();
    this.scene.restart();
  }

  private quitToTitle(): void {
    if (this.disposed) return;
    if (shouldRestartOnQuit(SceneKey.InfiniteWar)) {
      this.teardown();
      this.scene.restart();
      return;
    }
    assertTransition(SceneKey.InfiniteWar, SceneKey.Title);
    this.teardown();
    this.scene.start(SceneKey.Title);
  }

  private teardown(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.menuOpen = false;
    this.castSession = IDLE_CAST;
    this.input.off("pointerdown", this.onPointerDown, this);
    try {
      this.hud?.destroy();
    } catch (err) {
      console.error("[infinite-war] hud destroy", err);
    }
    destroyUnitViews(this.unitsHost());
    destroyCardTexts(this.cardsHost);
    clearOverlay();
  }
}
